import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';
import type { PendingContentItem } from '../../lib/api/pending-api-client';

/**
 * Pending-content-approval slice.
 *
 * Holds the admin queue view: the current page of pending items, the
 * paging cursor, and per-operation lifecycle flags for
 * list / submit / approve / reject.
 */

export type QueueStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

/**
 * Per-date admin day-preview cache — keyed by Pacific-YMD. Populated
 * by the `fetchPendingByDate` thunk which calls
 * `POST /ListPendingContentV1` with an `intendedPublishDate` filter
 * so the response includes items of EVERY status (pending +
 * approved + rejected + system-auto-approved + denied) for that
 * day. The admin daily-page overlay renders this cache filtered by
 * the viewed date.
 */
export interface PendingByDateEntry {
  readonly status: QueueStatus;
  readonly items: ReadonlyArray<PendingContentItem>;
  readonly error?: string;
}

export interface PendingState {
  readonly listStatus: QueueStatus;
  readonly submitStatus: QueueStatus;
  readonly approveStatus: QueueStatus;
  readonly rejectStatus: QueueStatus;
  readonly items: ReadonlyArray<PendingContentItem>;
  readonly page: number;
  readonly total: number;
  readonly hasMore: boolean;
  readonly listError?: string;
  readonly submitError?: string;
  readonly actionError?: string;
  /**
   * Per-date admin day-preview cache — keyed by Pacific-YMD. Landed
   * 2026-09-12 to give admins full visibility over any date's
   * queue traffic across every status.
   */
  readonly byDate: Readonly<Record<string, PendingByDateEntry>>;
}

const initialState: PendingState = {
  listStatus: 'idle',
  submitStatus: 'idle',
  approveStatus: 'idle',
  rejectStatus: 'idle',
  items: [],
  page: 1,
  total: 0,
  hasMore: false,
  byDate: {},
};

export const pendingSlice = createSlice({
  name: 'pending',
  initialState,
  reducers: {
    pendingListRequested(state) {
      state.listStatus = 'loading';
      state.listError = undefined;
    },
    pendingListSucceeded(
      state,
      action: PayloadAction<{
        items: ReadonlyArray<PendingContentItem>;
        page: number;
        total: number;
        hasMore: boolean;
      }>,
    ) {
      state.listStatus = 'succeeded';
      state.items = castDraft(action.payload.items);
      state.page = action.payload.page;
      state.total = action.payload.total;
      state.hasMore = action.payload.hasMore;
    },
    pendingListFailed(state, action: PayloadAction<{ error: string }>) {
      state.listStatus = 'failed';
      state.listError = action.payload.error;
    },
    pendingSubmitRequested(state) {
      state.submitStatus = 'loading';
      state.submitError = undefined;
    },
    pendingSubmitSucceeded(state) {
      state.submitStatus = 'succeeded';
    },
    pendingSubmitFailed(state, action: PayloadAction<{ error: string }>) {
      state.submitStatus = 'failed';
      state.submitError = action.payload.error;
    },
    pendingActionRequested(state, action: PayloadAction<{ kind: 'approve' | 'reject' }>) {
      state.actionError = undefined;
      if (action.payload.kind === 'approve') {
        state.approveStatus = 'loading';
      } else {
        state.rejectStatus = 'loading';
      }
    },
    pendingActionSucceeded(state, action: PayloadAction<{ id: string; kind: 'approve' | 'reject' }>) {
      // Drop the acted-on item from the /admin/pending page so the
      // pending review queue updates without a full re-fetch. Total
      // shrinks by one; the caller MAY re-fetch to refresh cursors.
      state.items = state.items.filter((item) => item.id !== action.payload.id);
      state.total = Math.max(0, state.total - 1);
      // On the admin day-preview cache, MUTATE the acted-on item's
      // status to reflect the terminal state instead of dropping it.
      // The admin overlay renders every status side-by-side (2026-
      // 09-12 admin day-preview change); dropping approved items
      // from the cache would defeat that visibility.
      const newStatus = action.payload.kind === 'approve' ? 'approved' : 'rejected';
      const nextByDate: Record<string, PendingByDateEntry> = {};
      for (const date of Object.keys(state.byDate)) {
        const entry = state.byDate[date]!;
        nextByDate[date] = {
          status: entry.status,
          items: entry.items.map((it) =>
            it.id === action.payload.id ? { ...it, status: newStatus as PendingContentItem['status'] } : it,
          ),
          error: entry.error,
        };
      }
      state.byDate = castDraft(nextByDate);
      if (action.payload.kind === 'approve') {
        state.approveStatus = 'succeeded';
      } else {
        state.rejectStatus = 'succeeded';
      }
    },
    pendingActionFailed(
      state,
      action: PayloadAction<{ error: string; kind: 'approve' | 'reject' }>,
    ) {
      state.actionError = action.payload.error;
      if (action.payload.kind === 'approve') {
        state.approveStatus = 'failed';
      } else {
        state.rejectStatus = 'failed';
      }
    },
    pendingSubmitReset(state) {
      state.submitStatus = 'idle';
      state.submitError = undefined;
    },
    pendingByDateRequested(state, action: PayloadAction<{ date: string }>) {
      const prior = state.byDate[action.payload.date];
      state.byDate = castDraft({
        ...state.byDate,
        [action.payload.date]: {
          status: 'loading',
          items: prior?.items ?? [],
        },
      });
    },
    pendingByDateSucceeded(
      state,
      action: PayloadAction<{
        date: string;
        items: ReadonlyArray<PendingContentItem>;
      }>,
    ) {
      state.byDate = castDraft({
        ...state.byDate,
        [action.payload.date]: {
          status: 'succeeded',
          items: action.payload.items,
        },
      });
    },
    pendingByDateFailed(
      state,
      action: PayloadAction<{ date: string; error: string }>,
    ) {
      const prior = state.byDate[action.payload.date];
      state.byDate = castDraft({
        ...state.byDate,
        [action.payload.date]: {
          status: 'failed',
          items: prior?.items ?? [],
          error: action.payload.error,
        },
      });
    },
    pendingBodyUpdated(
      state,
      action: PayloadAction<{
        id: string;
        body: string;
        version: number;
        lastActionAt: string;
      }>,
    ) {
      // Replace the body + version on the matching item across every
      // byDate cache entry. The /admin/pending list is not updated —
      // its items still surface with the old body until the next
      // fetchPendingList() call. Landed 2026-09-13 (edit-in-queue).
      const nextByDate: Record<string, PendingByDateEntry> = {};
      for (const date of Object.keys(state.byDate)) {
        const entry = state.byDate[date]!;
        nextByDate[date] = {
          status: entry.status,
          items: entry.items.map((it) =>
            it.id === action.payload.id
              ? { ...it, body: action.payload.body, version: action.payload.version }
              : it,
          ),
          error: entry.error,
        };
      }
      state.byDate = castDraft(nextByDate);
    },
  },
});

export const {
  pendingListRequested,
  pendingListSucceeded,
  pendingListFailed,
  pendingSubmitRequested,
  pendingSubmitSucceeded,
  pendingSubmitFailed,
  pendingActionRequested,
  pendingActionSucceeded,
  pendingActionFailed,
  pendingSubmitReset,
  pendingByDateRequested,
  pendingByDateSucceeded,
  pendingByDateFailed,
  pendingBodyUpdated,
} = pendingSlice.actions;

export default pendingSlice.reducer;
